'use client';

import React, { useState, useEffect } from 'react';
import DashboardKPIs from './DashboardKPIs';
import ListingsTable from './ListingsTable';
import AddListingModal from './AddListingModal';
import { OwnerListing } from '@/lib/ownerListings';
import { listingService, DBListing } from '@/lib/services/dataService';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, LayoutDashboard, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useRealtimeListings } from '@/hooks/useRealtimeListings';
import { useRealtimeInquiries } from '@/hooks/useRealtimeInquiries';

function dbToOwnerListing(l: DBListing): OwnerListing {
  return {
    id: l.id,
    title: l.title,
    houseType: l.house_type,
    bhk: l.bhk,
    bedrooms: l.bedrooms,
    bathrooms: l.bathrooms,
    builtUpArea: l.built_up_area,
    furnishing: l.furnishing,
    parking: l.parking,
    waterFacility: l.water_facility,
    powerBackup: l.power_backup,
    lift: l.lift,
    wifi: l.wifi,
    floorNumber: l.floor_number,
    facing: l.facing,
    availableFrom: l.available_from,
    availableFromRaw: l.available_from,
    monthlyRent: l.monthly_rent,
    securityDeposit: l.security_deposit,
    maintenanceCharges: l.maintenance_charges,
    negotiable: l.negotiable,
    address: l.address,
    locality: l.locality,
    city: l.city,
    state: l.state,
    pincode: l.pincode,
    landmarks: l.landmarks,
    googleMapsLink: l.google_maps_link,
    photos: l.photos?.length ? l.photos : [],
    coverPhoto: l.cover_photo || '',
    status: l.status as OwnerListing['status'],
    views: l.views,
    inquiries: l.inquiries_count,
    listedOn: l.listed_on || '',
    verified: l.verified,
    featured: l.featured,
    ownerName: l.owner_name,
    ownerPhone: l.owner_phone,
    ownerEmail: l.owner_email,
    expiresAt: l.expires_at,
    listingNumber: l.listing_number,
  };
}

// Check if listing is expired or expiring soon (within 3 days)
function getExpiryStatus(expiresAt: string | null): { expired: boolean; expiringSoon: boolean; daysLeft: number } {
  if (!expiresAt) return { expired: false, expiringSoon: false, daysLeft: 30 };
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return {
    expired: daysLeft <= 0,
    expiringSoon: daysLeft > 0 && daysLeft <= 3,
    daysLeft: Math.max(0, daysLeft),
  };
}

export default function OwnerDashboardContent() {
  const { user } = useAuth();
  const [listings, setListings] = useState<OwnerListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editListing, setEditListing] = useState<OwnerListing | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchListings = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const data = await listingService.getOwnerListings(user.id);
      setListings(data.map(dbToOwnerListing));
    } catch {
      toast.error('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, [user?.id]);

  // ── Re-fetch on window focus so counts are always fresh ──────────────────────
  useEffect(() => {
    const handleFocus = () => {
      if (user?.id) fetchListings();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user?.id]);

  // ── Real-time: owner's own listings (status changes from admin) ──────────────
  useRealtimeListings({
    ownerId: user?.id,
    onInsert: (newListing) => {
      setListings((prev) => {
        if (prev.some((l) => l.id === newListing.id)) return prev;
        return [dbToOwnerListing(newListing), ...prev];
      });
    },
    onUpdate: (updated) => {
      setListings((prev) => {
        const prevListing = prev.find((l) => l.id === updated.id);
        const prevStatus = prevListing?.status;
        const newStatus = updated.status;
        // Notify owner when admin approves or rejects
        if (prevStatus === 'Pending Review' && newStatus === 'Active') {
          toast.success(`🎉 Your listing "${updated.title}" has been approved and is now live!`);
        } else if (prevStatus === 'Pending Review' && newStatus === 'Rejected') {
          toast.error(`Your listing "${updated.title}" was rejected. Check the listing for details.`);
        }
        return prev.map((l) => (l.id === updated.id ? dbToOwnerListing(updated) : l));
      });
    },
    onDelete: (id) => {
      setListings((prev) => prev.filter((l) => l.id !== id));
    },
  });

  // ── Real-time: new inquiries on owner's listings ─────────────────────────────
  useRealtimeInquiries({
    ownerListingIds: listings.map((l) => l.id),
    onInsert: (inquiry) => {
      // Increment inquiries_count for the matching listing in local state
      setListings((prev) =>
        prev.map((l) =>
          l.id === inquiry.listing_id
            ? { ...l, inquiries: (l.inquiries || 0) + 1 }
            : l
        )
      );
      // Find the listing title for a richer notification
      const matchedListing = listings.find((l) => l.id === inquiry.listing_id);
      const listingTitle = matchedListing?.title || 'your listing';
      const tenantName = inquiry.tenant_name || 'A tenant';
      toast.info(`📩 ${tenantName} sent an inquiry for "${listingTitle}"`, {
        description: inquiry.message || 'Tap to view in your dashboard',
        duration: 6000,
      });
    },
  });

  const handleDelete = (id: string) => {
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      const ok = await listingService.deleteListing(deleteId);
      if (ok) {
        setListings((prev) => prev.filter((l) => l.id !== deleteId));
        toast.success('Listing deleted successfully');
      } else {
        toast.error('Failed to delete listing');
      }
    } catch {
      toast.error('Failed to delete listing');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  const handleStatusChange = async (id: string, status: OwnerListing['status']) => {
    try {
      await listingService.updateListing(id, { status: status as DBListing['status'] });
      setListings((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Listing status updated to "${status}"`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleAddListing = async (newListing: OwnerListing) => {
    if (!user?.id) return;
    try {
      const created = await listingService.createListing({
        owner_id: user.id,
        title: newListing.title,
        house_type: newListing.houseType,
        bhk: newListing.bhk,
        bedrooms: newListing.bedrooms,
        bathrooms: newListing.bathrooms,
        built_up_area: newListing.builtUpArea,
        furnishing: newListing.furnishing,
        parking: newListing.parking,
        water_facility: newListing.waterFacility,
        power_backup: newListing.powerBackup,
        lift: newListing.lift,
        wifi: newListing.wifi,
        floor_number: newListing.floorNumber,
        facing: newListing.facing,
        available_from: newListing.availableFrom,
        available_from_raw: newListing.availableFromRaw,
        monthly_rent: newListing.monthlyRent,
        security_deposit: newListing.securityDeposit,
        maintenance_charges: newListing.maintenanceCharges,
        negotiable: newListing.negotiable,
        address: newListing.address,
        locality: newListing.locality,
        city: newListing.city,
        state: newListing.state,
        pincode: newListing.pincode,
        landmarks: newListing.landmarks,
        google_maps_link: newListing.googleMapsLink,
        photos: newListing.photos,
        cover_photo: newListing.coverPhoto,
        status: 'Draft',
        views: 0,
        inquiries_count: 0,
        verified: false,
        featured: false,
        owner_name: user?.user_metadata?.full_name || newListing.ownerName,
        owner_phone: user?.user_metadata?.phone || newListing.ownerPhone,
        owner_email: user?.email || newListing.ownerEmail,
        rejection_reason: '',
        reviewed_at: '',
        listed_on: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      });
      if (created) {
        setListings((prev) => [dbToOwnerListing(created), ...prev]);
        // Redirect to payment with the real listing ID
        const LISTING_CHARGES: Record<string, number> = {
          'Independent House': 299, 'Room': 299, 'Apartment': 399,
          'Commercial Room': 499, 'Commercial Office': 499, 'Villa': 799, 'Studio': 299,
        };
        const amount = LISTING_CHARGES[newListing.houseType] ?? 299;
        const params = new URLSearchParams({
          title: newListing.title,
          type: newListing.houseType,
          amount: String(amount),
          listingId: created.id,
        });
        window.location.href = `/payment?${params.toString()}`;
      } else {
        toast.error('Failed to submit listing');
      }
    } catch {
      toast.error('Failed to submit listing');
    }
    setAddModalOpen(false);
    setEditListing(null);
  };

  const expiredListings = listings.filter((l) => {
    const { expired } = getExpiryStatus((l as any).expiresAt);
    return expired && l.status === 'Active';
  });
  const expiringSoonListings = listings.filter((l) => {
    const { expiringSoon } = getExpiryStatus((l as any).expiresAt);
    return expiringSoon && l.status === 'Active';
  });

  const handleRenew = (listing: OwnerListing) => {
    const params = new URLSearchParams({
      title: listing.title,
      type: listing.houseType,
      amount: '299',
      listingId: listing.id,
      renew: 'true',
    });
    window.location.href = `/payment?${params.toString()}`;
  };

  const activeCount = listings.filter((l) => l.status === 'Active').length;
  const pendingCount = listings.filter((l) => l.status === 'Pending Review').length;
  const totalViews = listings.reduce((sum, l) => sum + l.views, 0);
  const totalInquiries = listings.reduce((sum, l) => sum + l.inquiries, 0);

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8 space-y-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <LayoutDashboard size={20} className="text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Owner Dashboard</h1>
            <p className="text-sm text-muted-foreground">
              Manage your rental listings and track performance
            </p>
          </div>
        </div>
        <button
          onClick={() => setAddModalOpen(true)}
          className="btn-primary shrink-0"
        >
          <Plus size={18} />
          Add New Listing
        </button>
      </div>

      {/* Expiry Alerts */}
      {expiredListings.length > 0 && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 space-y-3">
          <div className="flex items-center gap-2 text-red-700 font-bold text-sm">
            <AlertTriangle size={16} />
            {expiredListings.length} listing{expiredListings.length > 1 ? 's' : ''} expired — hidden from browse
          </div>
          <div className="space-y-2">
            {expiredListings.map((l) => (
              <div key={`exp-${l.id}`} className="flex items-center justify-between gap-3 bg-white rounded-xl px-4 py-3 border border-red-100">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate">{l.title}</div>
                  <div className="text-xs text-red-600">Listing expired — renew to make it visible again</div>
                </div>
                <button
                  onClick={() => handleRenew(l)}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600 text-white text-xs font-bold hover:bg-red-700 transition-colors"
                >
                  <RefreshCw size={12} />Renew
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {expiringSoonListings.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-3">
          <div className="flex items-center gap-2 text-amber-700 font-bold text-sm">
            <AlertTriangle size={16} />
            {expiringSoonListings.length} listing{expiringSoonListings.length > 1 ? 's' : ''} expiring soon
          </div>
          <div className="space-y-2">
            {expiringSoonListings.map((l) => {
              const { daysLeft } = getExpiryStatus((l as any).expiresAt);
              return (
                <div key={`exp-soon-${l.id}`} className="flex items-center justify-between gap-3 bg-white rounded-xl px-4 py-3 border border-amber-100">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">{l.title}</div>
                    <div className="text-xs text-amber-600">Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}</div>
                  </div>
                  <button
                    onClick={() => handleRenew(l)}
                    className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 transition-colors"
                  >
                    <RefreshCw size={12} />Renew
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <DashboardKPIs
        activeCount={activeCount}
        pendingCount={pendingCount}
        totalViews={totalViews}
        totalInquiries={totalInquiries}
        totalListings={listings.length}
      />

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={28} className="animate-spin text-primary" />
        </div>
      ) : (
        <ListingsTable
          listings={listings}
          onEdit={setEditListing}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Add Listing Modal */}
      <AddListingModal
        isOpen={addModalOpen || editListing !== null}
        onClose={() => {
          setAddModalOpen(false);
          setEditListing(null);
        }}
        onSubmit={handleAddListing}
        editListing={editListing}
      />

      {/* Delete Confirm Modal */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteId(null)}
            aria-hidden="true"
          />
          <div className="relative bg-card rounded-2xl border border-border shadow-modal p-6 max-w-sm w-full slide-up">
            <h3 className="text-base font-bold text-foreground mb-2">Delete Listing?</h3>
            <p className="text-sm text-muted-foreground mb-6">
              This will permanently remove the listing and all its photos. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="btn-secondary flex-1 justify-center"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteLoading}
                className="btn-danger flex-1 justify-center bg-red-600 text-white border-red-600 hover:bg-red-700 flex items-center gap-2"
              >
                {deleteLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                Delete Listing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}